import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { FiDownload, FiSearch, FiX } from 'react-icons/fi';
import { getAllScreeners, viewScreenerDetails, exportToCSV } from '../../services/scanner/scannerApi';
import FilterPanel from './components/FilterPanel';
import StockInfoCard from './components/StockInfoCard';
import StocksTable from './components/StocksTable';
import StocksTableSkeleton from './components/StocksTableSkeleton';
import AddMultipleToWatchlistModal from '../../components/watchlist/components/AddMultipleToWatchlistModal';
import { toast } from 'react-toastify';
import emptyBasket from "../../images/empty_basket.svg";
import wlUnselected from "../../images/icons/wl_selected.svg";
import wlSelected from "../../images/icons/wl_unselected.svg";

/**
 * Main Scanners page component
 * Two-column layout: Filters on left, Data on right
 */
const NewScanners = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // State for screeners
  const [screeners, setScreeners] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for selected filter - restore from sessionStorage
  const [selectedFilter, setSelectedFilter] = useState(() => {
    const saved = sessionStorage.getItem('scanners_selectedFilter');
    return saved ? JSON.parse(saved) : null;
  });
  const [screenerData, setScreenerData] = useState(null);
  const [filteredTableData, setFilteredTableData] = useState([]);
  const [loadingScreenerData, setLoadingScreenerData] = useState(false);

  // State for table filters - restore from sessionStorage
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTimeframe, setSelectedTimeframe] = useState(() => {
    return sessionStorage.getItem('scanners_selectedTimeframe') || '1day';
  });
  const [selectedGroup, setSelectedGroup] = useState(() => {
    return sessionStorage.getItem('scanners_selectedGroup') || 'NIFTY 50';
  });

  // State for stock selection
  const [selectedStocks, setSelectedStocks] = useState(new Set());

  // State for watchlist popup
  const [showWatchlistPopup, setShowWatchlistPopup] = useState(false);

  // Restore stock page URL if returning from navbar (not from back button or "Back to Scanners")
  useEffect(() => {
    // Check if we should prevent redirect (back button or "Back to Scanners" was used)
    const preventRedirect = sessionStorage.getItem('scanners_preventRedirect');

    // Clear the flag if it exists
    if (preventRedirect) {
      sessionStorage.removeItem('scanners_preventRedirect');
    }

    // Only redirect if there's a stored stock URL AND no prevent flag was set
    if (!preventRedirect) {
      const lastStockUrl = sessionStorage.getItem('scanners_lastStockUrl');
      if (lastStockUrl) {
        // Navigate to the last stock page
        navigate(lastStockUrl, { replace: true });
      }
    }
  }, []);

  // Load all screeners on mount
  useEffect(() => {
    loadAllScreeners();
  }, []);

  // Save selected filter to sessionStorage when it changes
  useEffect(() => {
    if (selectedFilter) {
      sessionStorage.setItem('scanners_selectedFilter', JSON.stringify(selectedFilter));
    }
  }, [selectedFilter]);

  // Save timeframe and group to sessionStorage when they change
  useEffect(() => {
    sessionStorage.setItem('scanners_selectedTimeframe', selectedTimeframe);
  }, [selectedTimeframe]);

  useEffect(() => {
    sessionStorage.setItem('scanners_selectedGroup', selectedGroup);
  }, [selectedGroup]);

  // Load screener details when filter or filters change
  useEffect(() => {
    if (selectedFilter && selectedFilter.name) {
      loadScreenerDetails();
    }
  }, [selectedFilter, selectedTimeframe, selectedGroup]);

  // Filter table data based on search term
  useEffect(() => {
    const tableData = screenerData?.table_data || screenerData?.tableData;
    if (!tableData) {
      setFilteredTableData([]);
      return;
    }

    if (!searchTerm.trim()) {
      setFilteredTableData(tableData);
      return;
    }

    const lowerSearch = searchTerm.toLowerCase();
    const filtered = tableData.filter(
      (stock) =>
        stock.stock?.toLowerCase().includes(lowerSearch) ||
        stock.company_name?.toLowerCase().includes(lowerSearch) ||
        stock.companyName?.toLowerCase().includes(lowerSearch)
    );
    setFilteredTableData(filtered);
  }, [searchTerm, screenerData]);

  const loadAllScreeners = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load all three categories in parallel
      const [candlesticks, scanners, technicals] = await Promise.all([
        getAllScreeners('candlesticks'),
        getAllScreeners('scanners'),
        getAllScreeners('technicals'),
      ]);

      console.log('Loaded screeners:', { candlesticks, scanners, technicals });

      setScreeners({
        candlesticks,
        scanners,
        technicals,
      });
    } catch (err) {
      console.error('Error loading screeners:', err);
      setError('Failed to load screeners. Please try again.');
      toast.error('Failed to load screeners');
    } finally {
      setLoading(false);
    }
  };

  const loadScreenerDetails = async () => {
    if (!selectedFilter || !selectedFilter.name || !selectedFilter.path) {
      console.error('Missing required filter parameters:', selectedFilter);
      return;
    }

    setLoadingScreenerData(true);
    setError(null);

    try {
      const data = await viewScreenerDetails({
        name: selectedFilter.name,
        path: selectedFilter.path,
        selected_group: selectedGroup,
        selected_timeframe: selectedTimeframe,
      });

      console.log('Screener details loaded:', data);
      setScreenerData(data);
      setFilteredTableData(data.table_data || data.tableData || []);
      setSelectedStocks(new Set());
    } catch (err) {
      console.error('Error loading screener details:', err);
      setError('Failed to load screener data. Please try again.');
      toast.error('Failed to load screener data');
    } finally {
      setLoadingScreenerData(false);
    }
  };

  const handleFilterSelect = (filter) => {
    console.log('Filter selected:', filter);
    setSelectedFilter(filter);
    setSearchTerm('');
    setSelectedStocks(new Set());
  };

  const handleReset = () => {
    setSelectedFilter(null);
    setScreenerData(null);
    setFilteredTableData([]);
    setSearchTerm('');
    setSelectedStocks(new Set());
    setSelectedTimeframe('1day');
    setSelectedGroup('NIFTY 50');
  };

  const handleStockSelect = (stock) => {
    setSelectedStocks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stock.stock)) {
        newSet.delete(stock.stock);
      } else {
        newSet.add(stock.stock);
      }
      return newSet;
    });
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedStocks(new Set(filteredTableData.map((s) => s.stock)));
    } else {
      setSelectedStocks(new Set());
    }
  };

  const handleDownloadCSV = () => {
    if (!filteredTableData || filteredTableData.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const filename = `${selectedFilter?.name || 'scanner'}_${new Date().toISOString().split('T')[0]}.csv`;
    exportToCSV(filteredTableData, filename);
    toast.success('Data exported successfully');
  };

  const handleAddToWatchlist = () => {
    if (selectedStocks.size === 0) {
      toast.warning('Please select at least one stock');
      return;
    }

    setShowWatchlistPopup(true);
  };

  const getSelectedInstruments = () => {
    return filteredTableData
      .filter((stock) => selectedStocks.has(stock.stock))
      .map((stock) => ({
        tradingSymbol: stock.stock,
        exchange: stock.exchange,
        code: stock.code,
        companyName: stock.company_name || stock.companyName,
      }));
  };

  if (loading && !screeners.candlesticks) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading scanners...</p>
        </div>
      </div>
    );
  }

  if (error && !screeners.candlesticks) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button
            onClick={loadAllScreeners}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-row items-start gap-5 pb-[60px] px-2 mt-12 `}
      style={{ minHeight: 'calc(100vh - 4rem)' }}
    >
      {/* Left Column: Filters */}
      <FilterPanel
        screeners={screeners}
        selectedFilter={selectedFilter}
        onFilterSelect={handleFilterSelect}
        onReset={handleReset}
      />

      {/* Right Column: Data */}
      <div className="flex-1 overflow-y-auto mt-3">
        {!selectedFilter ? (
          <div
            className={`rounded-[6px] w-full min-h-[600px] flex items-center justify-center ${theme === 'dark'
              ? 'bg-[#0F0F0F] shadow-[0_2px_5px_1px_rgba(175,175,175,0.1)]'
              : 'bg-white shadow-[0_2px_5px_1px_rgba(87,87,87,0.1)]'
              }`}
          >
            <div className="text-center w-[188px]">
              <div className="mb-4 flex justify-center">
                <img
                  src={emptyBasket}
                  alt="Empty Basket"
                  className="empty-widget-image"
                />
              </div>
              <p
                className={`text-sm font-medium ${theme === 'dark' ? 'text-[#7F7F7F]' : 'text-[#878787]'
                  }`}
              >
                Please modify/apply filters to find available stocks.
              </p>
            </div>
          </div>

        ) : (
          <>
            {/* Stock Info Card */}
            <StockInfoCard
              screener={selectedFilter.data}
              stockCount={(screenerData?.table_data || screenerData?.tableData)?.length || 0}
            />

            {/* Controls */}
            <div className="py-4">
              <div className="flex flex-row items-center gap-2">
                {/* Search */}
                <div className="flex-grow">
                  <div className="relative">
                    <FiSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-[15px] h-[13px] ${theme === 'dark' ? 'text-[#979797]' : 'text-[#6F6F6F]'
                      }`} />
                    <input
                      type="text"
                      placeholder={screenerData?.search_placeholder || screenerData?.searchPlaceholder || 'Search stocks...'}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className={`w-full h-9 pl-10 pr-10 rounded-[6px] border text-sm font-medium ${theme === 'dark'
                        ? 'bg-[#0F0F0F] border-[#373737] text-white placeholder-[#5B5B5B]'
                        : 'bg-white border-[#CFCFCF] text-black placeholder-[#5B5B5B]'
                        } focus:outline-none`}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm('')}
                        className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${theme === 'dark' ? 'text-[#979797]' : 'text-[#6F6F6F]'
                          }`}
                      >
                        <FiX size={13} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Timeframe Dropdown */}
                {screenerData?.timeframes && screenerData.timeframes.length > 0 && (
                  <select
                    value={selectedTimeframe}
                    onChange={(e) => setSelectedTimeframe(e.target.value)}
                    className={`h-9 px-3 rounded-[6px] border text-sm font-medium ${theme === 'dark'
                      ? 'bg-[#0F0F0F] border-[#373737] text-[#AFAFAF]'
                      : 'bg-white border-[#CFCFCF] text-[#575757]'
                      } focus:outline-none`}
                  >
                    {screenerData.timeframes.map((tf, idx) => {
                      const value = typeof tf === 'object' ? (tf.value || tf.display) : tf;
                      const label = typeof tf === 'object' ? (tf.display || tf.label || tf.value) : tf;
                      return (
                        <option key={value || idx} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                )}

                {/* Stock Group Dropdown */}
                {(screenerData?.stock_groups || screenerData?.stockGroups) && (screenerData.stock_groups || screenerData.stockGroups).length > 0 && (
                  <select
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                    className={`h-9 w-[125px] px-3 rounded-[6px] border text-sm font-medium ${theme === 'dark'
                      ? 'bg-[#0F0F0F] border-[#373737] text-[#AFAFAF]'
                      : 'bg-white border-[#CFCFCF] text-[#575757]'
                      } focus:outline-none`}
                  >
                    {(screenerData.stock_groups || screenerData.stockGroups).map((group, idx) => {
                      const value = typeof group === 'object' ? (group.value || group.display) : group;
                      const label = typeof group === 'object' ? (group.display || group.label || group.value) : group;
                      return (
                        <option key={value || idx} value={value}>
                          {label}
                        </option>
                      );
                    })}
                  </select>
                )}

                {/* Download Button */}
                <button
                  onClick={handleDownloadCSV}
                  className={`h-9 px-3 rounded-[6px] flex items-center gap-2 text-xs font-semibold ${theme === 'dark'
                    ? 'bg-[#BCEF3F] text-[#0F0F0F]'
                    : 'bg-[#0F0F0F] text-white'
                    }`}
                >
                  <FiDownload size={16} />
                  Download
                </button>

                {/* Add to Watchlist Button */}
                <button
                  onClick={handleAddToWatchlist}
                  disabled={selectedStocks.size === 0}
                  className={`h-9 px-3 rounded-[6px] flex items-center gap-2 text-xs font-semibold ${selectedStocks.size === 0
                    ? theme === 'dark'
                      ? 'bg-[#4F4F4F] text-[#7F7F7F] cursor-not-allowed'
                      : 'bg-[#B7B7B7] text-[#878787] cursor-not-allowed'
                    : theme === 'dark'
                      ? 'bg-[#BCEF3F] text-[#0F0F0F]'
                      : 'bg-[#0F0F0F] text-white'
                    }`}
                >
                  <img
                    src={selectedStocks.size > 0 ? wlUnselected : wlSelected}
                    alt="Watchlist"
                    className="w-4 h-4"
                  />
                  Add to Watchlist
                  {/* {selectedStocks.size > 0 && (
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${theme === 'dark' ? 'bg-[#0F0F0F] text-[#BCEF3F]' : 'bg-white text-[#0F0F0F]'
                      }`}>
                      {selectedStocks.size}
                    </span>
                  )} */}
                </button>
              </div>
            </div>

            {/* Data Table */}
            <div className="mb-6">
              {loadingScreenerData ? (
                <StocksTableSkeleton />
              ) : (
                <StocksTable
                  data={filteredTableData}
                  selectedStocks={selectedStocks}
                  onStockSelect={handleStockSelect}
                  onSelectAll={handleSelectAll}
                />
              )}
            </div>
          </>
        )}
      </div>

      {/* Watchlist Popup */}
      <AddMultipleToWatchlistModal
        isOpen={showWatchlistPopup}
        onClose={() => setShowWatchlistPopup(false)}
        instruments={getSelectedInstruments()}
      />
    </div>
  );
};

export default NewScanners;
